import { isMagentaSTB, isSafariBrowser } from '@foundation-player/platform-check';
import { AdvertisingEvent } from '../advertising/event-types';
import { PlayerEvent } from '../event-emitter/events';
import { FoundationEventBase, MediaLoadEvent, SessionStart } from '../event-emitter/unfiltered';
import { Media } from '../media';
import { PlayerInterface } from '../player-interface';
import { ContentState, MediaState, SessionState } from './types';

/**
 * The MediaManager extracts some logic out of the FoundationPlayer.
 *
 * It handles starting a medium (should a pre-roll ad slot start first?); the
 * state of the player, the session and the content; as well as a fix for
 * stalling playout.
 *
 * Note: It could have well been a part of the FoundationPlayer, as it only
 * listens on player events and only calls public player methods. In addition,
 * the MediaManager is a private field of the player and thus cannot be accessed
 * from the outside.
 */
export class MediaManager {
  private media?: Media;
  private sessionState: SessionState = SessionState.NotYetStarted;
  private contentState?: ContentState;
  private mediaState?: MediaState;

  /**
   * True if any asset was successfully started in the player
   */
  private initiallyStarted = false;

  private mediaLoaded = false;
  private adLoaded = false;

  private readyEventFired = false;

  private autoplaySuccessful = false;

  private autoplayDisabled = false;

  private postRollDone = false;

  protected stallingTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly player: PlayerInterface) {
    this.addEventListeners(player);
  }

  getMedia() {
    return this.media;
  }

  /**
   * True if any asset was successfully started in the player
   */
  isInitiallyStarted() {
    return this.initiallyStarted;
  }

  /**
   * returns true if content is ready AND advertisement was initialized
   */
  isPlayerReady() {
    return this.mediaLoaded && this.adLoaded;
  }

  /**
   * Returns true if the autoplay was successful.
   */
  wasAutoplaySuccessful() {
    return this.autoplaySuccessful;
  }

  /**
   * Initializes playout and automatically starts content or advertising it if autoplay is enabled.
   */
  async initializePlayout(media: Media): Promise<void> {
    this.media = media;
    this.mediaLoaded = false;
    this.adLoaded = false;
    this.readyEventFired = false;
    this.postRollDone = false;
    this.autoplayDisabled = false;
    this.setContentState(ContentState.NotYetStarted);
    this.setMediaState(MediaState.NotYetStarted);
    await this.loadMedia();
  }

  startSession(sessionId: string) {
    this.setSessionState(SessionState.Started);
    this.player.dispatchEvent(new SessionStart(sessionId));
  }

  getSessionState(): SessionState | undefined {
    return this.sessionState;
  }

  getContentState(): ContentState | undefined {
    return this.contentState;
  }

  disableAutoplay() {
    this.autoplayDisabled = true;
  }

  private async loadMedia(): Promise<void> {
    if (!this.media) {
      return;
    }

    this.player.dispatchEvent(new MediaLoadEvent(PlayerEvent.MediaLoad, { mediaId: this.media.id }));

    /*
     * Content gets prepared before the advertisements because:
     * 1. User-Experience: If media load fails, they did not watch advertisements for nothing
     * 2. Performance: Content is already loaded, buffered and ready to go once the advertisements finish
     * 3. Performance: Switching between encrypted and non-encrypted content is faster in some players
     */
    await this.player.getPlayerAdapter().load(this.media);
    this.setContentState(ContentState.NotYetStarted);
    this.setMediaState(MediaState.NotYetStarted);
  }

  private async playLoadedMedia(): Promise<void> {
    if (this.canAutoplay()) {
      if (!this.autoplaySuccessful) {
        this.checkAutoplaySuccess();
      }
      await this.play();
    }
  }

  private checkAutoplaySuccess() {
    let previousTime = this.media?.startTime || 0;
    let watchTimeSeconds = 0;

    const trackAutoplay = () => {
      const currentTime = this.player.getCurrentTime();
      watchTimeSeconds = watchTimeSeconds + Math.max(currentTime - previousTime, 0);

      if (watchTimeSeconds >= 2) {
        this.autoplaySuccessful = true;
        this.player.dispatchEvent(new FoundationEventBase(PlayerEvent.AutoplaySuccessful));
        this.player.removeEventListener(PlayerEvent.TimeChanged, trackAutoplay);
      }
      previousTime = currentTime;
    };
    this.player.addEventListener(PlayerEvent.TimeChanged, trackAutoplay);
  }

  // Is used by the foundation player to
  // call the `play()` method and plays
  // either ads (if needed) or the content
  async playLoadedMediaManually(): Promise<void> {
    await this.play();
  }

  private selectLanguage() {
    const audioTracks = this.player.getAvailableAudioTracks();

    // prettier-ignore
    const selectedTrack =
      audioTracks.find((audioTrack) => audioTrack.language === this.player.getMediaConfig()?.audioLanguage) ??
      audioTracks.find((audioTrack) => audioTrack.language === this.player.getPlayerConfig().audioLanguage) ??
      audioTracks.find((audioTrack) => audioTrack.isMain) ??
      undefined;

    if (selectedTrack) {
      this.player.setAudioTrack(selectedTrack.id);
    }
  }

  private selectTextTrack() {
    const textTracks = this.player.getTextTracks();

    // prettier-ignore
    const selectedTextTracks =
      textTracks.find((textTrack) => textTrack.language === this.player.getMediaConfig()?.textTrackLanguage) ??
      textTracks.find((textTrack) => textTrack.language === this.player.getPlayerConfig().textTrackLanguage) ??
      textTracks.find((textTrack) => textTrack.language === this.player.getMediaConfig()?.subtitleLanguage) ??
      textTracks.find((textTrack) => textTrack.language === this.player.getPlayerConfig().subtitleLanguage) ??
      textTracks.find((textTrack) => textTrack.isActive) ??
      undefined;

    if (selectedTextTracks) {
      this.player.enableTextTrack(selectedTextTracks.id);
    }
  }

  private emitMediaStarted(): void {
    if (this.media && this.mediaState === MediaState.NotYetStarted) {
      this.setMediaState(MediaState.Started);
      this.player.dispatchEvent(new FoundationEventBase(PlayerEvent.MediaStarted));
    }
  }

  private emitContentStart(): void {
    // For livestreams the root payer e.g. dash.js will continuously emit time changed events even when the content is not yet started.
    // This is because the current available window of the stream changes and the player adapts to it.
    // In those cases player.isPlaying() will return false, and we should not emit the content start event.
    if (this.media && this.player.isPlaying() && this.contentState === ContentState.NotYetStarted && !this.isAd()) {
      this.setContentState(ContentState.Started);
      this.player.dispatchEvent(new FoundationEventBase(PlayerEvent.ContentStart));
      // set a longer timeout here to avoid seeking while the player may still be buffering
      this.startStallingTimer(3000);
    }
  }

  private emitContentEnd(): void {
    if (!this.isAd() && this.contentState === ContentState.Started) {
      this.setContentState(ContentState.Ended);
      this.player.dispatchEvent(new FoundationEventBase(PlayerEvent.ContentEnd));
    }
  }

  private emitSessionEnd(): void {
    if (!this.isAd() && this.sessionState === SessionState.Started && this.contentState === ContentState.Ended) {
      this.setSessionState(SessionState.Ended);
      this.player.dispatchEvent(new FoundationEventBase(PlayerEvent.SessionEnd));
    }
  }

  private async preparePlayer(): Promise<void> {
    if (this.isPlayerReady()) {
      return;
    }

    const advertisingAdapter = this.player.getAdvertisingAdapter();
    const preRollSlot = advertisingAdapter?.getAdSlot(0);

    if (advertisingAdapter && preRollSlot && !preRollSlot.isInitialised()) {
      // we do not want to initAdSlot with autoplay=true since the media manager will handle that if necessary in play()
      await advertisingAdapter.initAdSlot(preRollSlot, false);
    } else {
      // if no advertisement is configured the advertisement is considered ready
      this.player.dispatchEvent(new FoundationEventBase(AdvertisingEvent.AdSlotReady));
    }
  }

  // This stalling fix mechanism is dedicated to a hardware bug happening on magenta boxes only.
  // The player gets incorrect buffer values when actually a buffer underrun happens. Therefore the player cannot react as expected and stalls as a result.
  // The seek of 100ms forward is the easiest workaround to get the player back to playing again. Using 0.1 seconds is big enough to not be discarded by the player.
  private startStallingTimer(timeout = 2000) {
    this.clearStallingTimeout();
    this.stallingTimeout = setTimeout(() => {
      if (
        !this.isAd() &&
        this.contentState === ContentState.Started &&
        this.mediaState === MediaState.Started &&
        this.player.isPlaying()
      ) {
        this.player.dispatchEvent(new FoundationEventBase(PlayerEvent.StallingFix));

        // Check for MagentaSTB AFTER emitting the event on purpose,
        // because we want to see the event in NPAW in the future to gather insights about the environments which execute the stallingFix.
        // Maybe it wasn't just the low-profile MagentaTV G5 box only. Let's see.

        // Why fencing the StallingFix to MagentaSTBs only?
        // Because we fear that clients with very low network bandwidth might get into an infinite loop.
        if (isMagentaSTB()) {
          this.player.seek(this.player.getCurrentTime() + 0.1);
        }
      }
    }, timeout);
  }

  private clearStallingTimeout() {
    clearTimeout(this.stallingTimeout);
    this.stallingTimeout = undefined;
  }

  private setContentState(contentState: ContentState) {
    this.contentState = contentState;
  }

  private setMediaState(mediaState: MediaState) {
    this.mediaState = mediaState;
  }

  private setSessionState(sessionState: SessionState) {
    this.sessionState = sessionState;
  }

  private async play(): Promise<void> {
    let shouldPlayAds = false;
    const advertisingAdapter = this.player.getAdvertisingAdapter();

    if (advertisingAdapter) {
      const adSlot = advertisingAdapter.getCurrentAdSlot();
      shouldPlayAds = advertisingAdapter.isAd() && !adSlot?.isActive();

      // do this before the media check to allow client to start
      // ad only players without content and autoplay
      if (shouldPlayAds) {
        await advertisingAdapter.startAdSlot();
      }
    }

    // if there are no ads and no content stop here
    if (!this.media) {
      return;
    }

    // only try content if ads were not started
    if (!shouldPlayAds) {
      await this.player.getPlayerAdapter().play();
    }

    this.initiallyStarted = true;
  }

  private isAd(): boolean {
    const advertisingAdapter = this.player.getAdvertisingAdapter();
    return advertisingAdapter?.isAd() || false;
  }

  canAutoplay() {
    if (!this.media) {
      return false;
    }

    return !this.autoplayDisabled && (this.initiallyStarted || this.media.autoplay);
  }

  private emitPlayerReady() {
    if (this.readyEventFired || !this.isPlayerReady()) {
      return;
    }

    this.readyEventFired = true;
    this.player.dispatchEvent(new FoundationEventBase(PlayerEvent.Ready));
  }

  private addEventListeners(player: PlayerInterface) {
    // This is a "fix" for a problem with smartclip and bitmovin
    // on safari browsers. If the player ends the postroll,
    // it will play around 2-3 seconds of the content till it stops.
    // We don't understand the cause, but this bandaid at least
    // reduces this to 0.2 seconds of play. It is not perfect,
    // but the best we can offer.
    if (isSafariBrowser() && player.getPlayerAdapter().getPlayerType() == 'Bitmovin') {
      player.addEventListener(PlayerEvent.TimeChanged, () => {
        if (this.postRollDone) {
          this.player.pause();
        }
      });
    }

    player.addEventListener(AdvertisingEvent.AdSlotCompleted, () => {
      const postRoll = this.player.getAdvertisingAdapter()?.getAdSlot(-1);
      const currentAdSlot = this.player.getAdvertisingAdapter()?.getCurrentAdSlot();

      if (postRoll === currentAdSlot) {
        this.postRollDone = true;
      }

      this.emitSessionEnd();
    });

    player.addEventListener(AdvertisingEvent.AdSlotReady, () => {
      this.adLoaded = true;
      if (this.isPlayerReady()) {
        this.emitPlayerReady();
      }
    });

    player.addEventListener(PlayerEvent.MediaLoaded, async () => {
      this.mediaLoaded = true;
      this.selectLanguage();
      this.selectTextTrack();

      await this.preparePlayer();
    });

    player.addEventListener(PlayerEvent.Ready, async () => {
      await this.playLoadedMedia();
      this.startStallingTimer();
    });

    player.addEventListener(PlayerEvent.PlaybackFinished, () => {
      this.emitContentEnd();
      const postRoll = player.getAdvertisingAdapter()?.getAdSlot(-1);
      if (!postRoll) {
        this.emitSessionEnd();
      }
    });

    player.addEventListener(PlayerEvent.Playing, () => {
      this.startStallingTimer();
    });

    player.addEventListener(PlayerEvent.StallEnded, () => {
      this.startStallingTimer();
    });

    player.addEventListener(PlayerEvent.TimeChanged, () => {
      this.emitMediaStarted();
      this.emitContentStart();
      // Ensure that the stalling fix works, even when no "StallEnded" event happened.
      // We assume that this behavior changed when switching from dashjs 4.5.0 to 4.7.0.
      // It was reproducible reliably on the low profile G5 Boxes within the first 10 minutes of content playback.
      this.startStallingTimer();
    });
  }
}
